import React, { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Notification from "../../components/Common/Notification";
import ActivityLoader from "../../components/Common/ActivityLoader";
import ajaxUser from "../../util/remote/ajaxUser";
import AlertVerifyEmail from "../../components/Welcome/AlertVerifyEmail";
import LeftAd from "../../components/Welcome/LeftAd";


const SignupPage = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repassword, setRepassword] = useState("");
  const [info, setInfo] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);


  const passwordInputType = useMemo(
    () => (showPwd ? "text" : "password"),
    [showPwd]
  );

  const handleSignup = async (verificationCode) => {
    if (loading) return;

    setLoading(true);
    const server_response = await ajaxUser.signup({
      first_name: firstName,
      surname,
      email,
      password,
      email_verification_code: verificationCode,
    });
    setLoading(false);

    if (server_response.status === "OK") {
      setInfo(
        <Notification
          message="Account created successfully! Redirecting to login..."
          type="success"
        />
      );
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    } else {
      setInfo(<Notification message={server_response?.message} type="danger" />);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!firstName.trim() || !surname.trim() || !email.trim()) {
      setInfo(<Notification message="Fill in all required fields" type="danger" />);
      return;
    }

    if (password.length < 6) {
      setInfo(
        <Notification
          message="Password should be at least 6 characters"
          type="danger"
        />
      );
      return;
    }

    if (password !== repassword) {
      setInfo(<Notification message="Passwords do not match" type="danger" />);
      return;
    }

    if (loading) return;

    // If verification already requested, just open the modal again
    if (codeRequested) {
      setShowAlert(true);
      return;
    }

    // Request email verification code
    setLoading(true);
    const server_response = await ajaxUser.sendEmailVerification(email);
    setLoading(false);

    if (server_response.status === "OK") {
      setCodeRequested(true);
      setShowAlert(true);
      setInfo(
        <Notification
          message={server_response.message}
          type="success"
          closable={false}
        />
      );
    } else {
      setInfo(
        <Notification
          message={server_response.message}
          type="danger"
          closable={false}
        />
      );
    }
  };

  const isLoggedIn = localStorage.getItem("ofc");
  if (isLoggedIn) return <Navigate to="/dashboard" />;

  return (
    <>
      <div className="ofLoginRoot">
        <div className="ofLoginBg" />

        <div className="ofLoginWrap">
          <div className="ofLoginGrid">
            {/* LEFT: Carousel / Benefits */}
            <LeftAd />

            {/* RIGHT: Signup Form */}
            <div className="ofLoginRight">
              <div className="ofLoginCard">
                <div className="ofLoginHeader">
                  <div className="ofLoginTitle">Create account</div>
                  <div className="ofLoginSub">
                    Sign up to manage vouchers, devices, and sales from one dashboard.
                  </div>
                </div>

                {info}
                {loading && <ActivityLoader />}

                <form onSubmit={handleSubmit} className="ofForm">
                  {/* Names */}
                  <div className="ofGrid2">
                    <div className="ofField">
                      <label htmlFor="firstName">First name</label>
                      <input
                        type="text"
                        id="firstName"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="ofField">
                      <label htmlFor="surname">Surname</label>
                      <input
                        type="text"
                        id="surname"
                        placeholder="Surname"
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="ofField">
                    <label htmlFor="email">Email address</label>
                    <input
                      type="email"
                      id="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>

                  {/* Passwords */}
                  <div className="ofGrid2">
                    <div className="ofField">
                      <label htmlFor="password">Create password</label>
                      <input
                        type={passwordInputType}
                        id="password"
                        placeholder="Create password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                    </div>

                    <div className="ofField">
                      <label htmlFor="repassword">Retype password</label>
                      <input
                        type={passwordInputType}
                        id="repassword"
                        placeholder="Retype password"
                        value={repassword}
                        onChange={(e) => setRepassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  <div className="ofRow" style={{ marginTop: 6 }}>
                    <label className="ofCheck">
                      <input
                        type="checkbox"
                        checked={showPwd}
                        onChange={() => setShowPwd((s) => !s)}
                      />
                      <span>Show password</span>
                    </label>

                    <Link to="/login" className="ofLink">
                      Already have an account?
                    </Link>
                  </div>

                  <button className="ofBtn" type="submit" disabled={loading}>
                    {showAlert
                      ? "Email verification..."
                      : loading
                      ? "Please wait..."
                      : "Create account"}
                  </button>

                  <div className="ofFooter">
                    <span>Already have an account?</span>{" "}
                    <Link to="/login" className="ofLinkStrong">
                      Sign in
                    </Link>
                  </div>
                </form>
              </div>

              <div className="ofLegal">
                By creating an account, you agree to our{" "}
                <span className="ofMuted">terms</span> and{" "}
                <span className="ofMuted">privacy policy</span>.
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertVerifyEmail
        show={showAlert}
        email={email}
        onClose={() => setShowAlert(false)}
        onConfirm={(code) => handleSignup(code)}
      />
    </>
  );
};

export default SignupPage;
